import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrophyIcon,
  StarIcon,
  FireIcon,
  AcademicCapIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  SparklesIcon,
  LockClosedIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import {
  TrophyIcon as TrophyIconSolid,
  StarIcon as StarIconSolid,
  FireIcon as FireIconSolid,
  AcademicCapIcon as AcademicCapIconSolid
} from '@heroicons/react/24/solid'
import { useAppSelector } from '../store'
import {
  useGetAllBadgesQuery,
  useGetUserBadgesQuery,
  useGetUnlockedBadgesQuery,
  useGetAvailableBadgesQuery,
  useGetBadgeProgressQuery,
  useCheckBadgeEligibilityMutation
} from '../store/api/gamificationApi'

// Badge kategorileri için ikonlar
const categoryIcons = {
  study: AcademicCapIcon,
  exam: TrophyIcon,
  streak: FireIcon,
  achievement: StarIcon,
  social: UserGroupIcon
}

const categorySolidIcons = {
  study: AcademicCapIconSolid,
  exam: TrophyIconSolid,
  streak: FireIconSolid,
  achievement: StarIconSolid,
  social: UserGroupIcon
}

// Premium Rarity renkleri ve tasarımları
const rarityColors = {
  common: {
    bg: 'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-slate-800 dark:via-gray-800 dark:to-slate-900',
    border: 'border-slate-200 dark:border-slate-600',
    text: 'text-slate-700 dark:text-slate-300',
    glow: 'shadow-lg shadow-slate-200/50 dark:shadow-slate-700/50',
    accent: 'bg-slate-500',
    shimmer: 'from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700'
  },
  rare: {
    bg: 'bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 dark:from-blue-900/30 dark:via-cyan-900/20 dark:to-blue-800/30',
    border: 'border-blue-300 dark:border-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    glow: 'shadow-xl shadow-blue-200/60 dark:shadow-blue-700/60',
    accent: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    shimmer: 'from-blue-200 to-cyan-300 dark:from-blue-600 dark:to-cyan-700'
  },
  epic: {
    bg: 'bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100 dark:from-purple-900/30 dark:via-violet-900/20 dark:to-purple-800/30',
    border: 'border-purple-400 dark:border-purple-500',
    text: 'text-purple-700 dark:text-purple-300',
    glow: 'shadow-xl shadow-purple-300/70 dark:shadow-purple-700/70',
    accent: 'bg-gradient-to-r from-purple-500 to-violet-600',
    shimmer: 'from-purple-200 to-violet-300 dark:from-purple-600 dark:to-violet-700'
  },
  legendary: {
    bg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100 dark:from-amber-900/40 dark:via-yellow-900/30 dark:to-orange-800/40',
    border: 'border-amber-400 dark:border-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    glow: 'shadow-2xl shadow-amber-300/80 dark:shadow-amber-600/80',
    accent: 'bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500',
    shimmer: 'from-amber-200 via-yellow-300 to-orange-300 dark:from-amber-600 dark:via-yellow-600 dark:to-orange-700'
  }
}

// Kategori renkleri
const categoryColors = {
  study: 'text-green-600 dark:text-green-400',
  exam: 'text-yellow-600 dark:text-yellow-400',
  streak: 'text-red-600 dark:text-red-400',
  achievement: 'text-purple-600 dark:text-purple-400',
  social: 'text-blue-600 dark:text-blue-400'
}

interface Badge {
  id: string
  name: string
  description: string
  icon: string
  color: string
  category: 'study' | 'exam' | 'streak' | 'achievement' | 'social'
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  unlockedAt?: string
  progress?: number
  maxProgress?: number
  conditions: {
    type: string
    target: number
    description: string
  }[]
  isActive: boolean
}

interface BadgeProgress {
  badgeId: string
  userId: string
  currentProgress: number
  maxProgress: number
  isCompleted: boolean
  completedAt?: string
}

const Badges: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth)
  const [activeTab, setActiveTab] = useState<'all' | 'unlocked' | 'available'>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedRarity, setSelectedRarity] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null)

  // API hooks
  const { data: allBadges = [], isLoading: isLoadingAll } = useGetAllBadgesQuery()
  
  const { data: unlockedBadges = [], isLoading: isLoadingUnlocked } = useGetUnlockedBadgesQuery(user?.uid || '', {
    skip: !user?.uid
  })
  
  const { data: availableBadges = [], isLoading: isLoadingAvailable } = useGetAvailableBadgesQuery(user?.uid || '', {
    skip: !user?.uid
  })
  
  const { data: badgeProgress = [], isLoading: isLoadingProgress } = useGetBadgeProgressQuery(
    { userId: user?.uid || '' },
    { skip: !user?.uid }
  )
  
  const [checkEligibility] = useCheckBadgeEligibilityMutation()

  // Aktif sekmeye göre badge listesi
  const getBadgeList = () => {
    switch (activeTab) {
      case 'unlocked':
        return unlockedBadges
      case 'available':
        return availableBadges
      default:
        return allBadges
    }
  }

  // Filtreleme
  const filteredBadges = getBadgeList().filter(badge => {
    const matchesSearch = badge.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         badge.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || badge.category === selectedCategory
    const matchesRarity = selectedRarity === 'all' || badge.rarity === selectedRarity
    
    return matchesSearch && matchesCategory && matchesRarity
  })

  // Badge progress'i al
  const getBadgeProgressData = (badgeId: string): BadgeProgress | undefined => {
    return badgeProgress.find(p => p.badgeId === badgeId)
  }

  // Progress yüzdesi hesapla
  const getProgressPercentage = (badge: Badge): number => {
    const progress = getBadgeProgressData(badge.id)
    if (!progress || progress.maxProgress === 0) return 0
    return Math.min((progress.currentProgress / progress.maxProgress) * 100, 100)
  }

  // Badge unlock kontrolü
  const handleCheckEligibility = async () => {
    if (user?.uid) {
      try {
        await checkEligibility(user.uid).unwrap()
      } catch (error) {
        console.error('Badge eligibility check failed:', error)
      }
    }
  }

  // Loading state
  const isLoading = isLoadingAll || isLoadingUnlocked || isLoadingAvailable || isLoadingProgress

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Rozetler yükleniyor...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg">
                <TrophyIconSolid className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Rozetler
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Başarılarınızı keşfedin ve yeni hedefler belirleyin
                </p>
              </div>
            </div>
            
            <button
              onClick={handleCheckEligibility}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <SparklesIcon className="h-5 w-5" />
              <span>Yeni Rozetleri Kontrol Et</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Rozet</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{allBadges.length}</p>
              </div>
              <TrophyIcon className="h-8 w-8 text-gray-400" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Kazanılan</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{unlockedBadges.length}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mevcut</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{availableBadges.length}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tamamlanma</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {allBadges.length > 0 ? Math.round((unlockedBadges.length / allBadges.length) * 100) : 0}%
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-purple-500" />
            </div>
          </motion.div>
        </div>

        {/* Tabs and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="p-6">
            {/* Tabs */}
            <div className="flex flex-wrap items-center justify-between mb-6">
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {[
                  { key: 'all', label: 'Tümü', count: allBadges.length },
                  { key: 'unlocked', label: 'Kazanılan', count: unlockedBadges.length },
                  { key: 'available', label: 'Mevcut', count: availableBadges.length }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      activeTab === tab.key
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                <FunnelIcon className="h-5 w-5" />
                <span>Filtreler</span>
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rozet ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Kategori
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">Tüm Kategoriler</option>
                      <option value="study">Çalışma</option>
                      <option value="exam">Sınav</option>
                      <option value="streak">Süreklilik</option>
                      <option value="achievement">Başarım</option>
                      <option value="social">Sosyal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nadir Seviye
                    </label>
                    <select
                      value={selectedRarity}
                      onChange={(e) => setSelectedRarity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">Tüm Seviyeler</option>
                      <option value="common">Yaygın</option>
                      <option value="rare">Nadir</option>
                      <option value="epic">Epik</option>
                      <option value="legendary">Efsanevi</option>
                    </select>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Badges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence>
            {filteredBadges.map((badge, index) => {
              const isUnlocked = badge.unlockedAt
              const progressData = getBadgeProgressData(badge.id)
              const progressPercentage = getProgressPercentage(badge)
              const CategoryIcon = categoryIcons[badge.category]
              const CategorySolidIcon = categorySolidIcons[badge.category]
              const rarityStyle = rarityColors[badge.rarity]

              return (
                <motion.div
                  key={badge.id}
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.9 }}
                  transition={{ 
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -8,
                    transition: { type: "spring", stiffness: 400, damping: 25 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedBadge(badge)}
                  className={`relative cursor-pointer group overflow-hidden ${
                    isUnlocked
                      ? `${rarityStyle.bg} ${rarityStyle.border} border-2 ${rarityStyle.glow}`
                      : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-2 border-gray-200 dark:border-gray-700 shadow-lg'
                  } rounded-2xl p-6 transition-all duration-500 backdrop-blur-sm`}
                >
                  {/* Shimmer Effect for Unlocked Badges */}
                  {isUnlocked && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className={`absolute inset-0 bg-gradient-to-r ${rarityStyle.shimmer} opacity-20 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000`} />
                    </div>
                  )}
                  
                  {/* Premium Rarity indicator */}
                  {isUnlocked && (
                    <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-full text-xs font-bold ${
                      rarityStyle.text
                    } bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border border-white/20 shadow-lg`}>
                      {badge.rarity === 'common' && '⚪ Yaygın'}
                      {badge.rarity === 'rare' && '🔵 Nadir'}
                      {badge.rarity === 'epic' && '🟣 Epik'}
                      {badge.rarity === 'legendary' && '🟡 Efsanevi'}
                    </div>
                  )}

                  {/* Premium Badge Icon */}
                  <div className="flex justify-center mb-6">
                    <div className={`relative p-6 rounded-2xl ${
                      isUnlocked
                        ? `bg-white/30 dark:bg-gray-900/30 backdrop-blur-sm border border-white/20 shadow-xl ${rarityStyle.glow}`
                        : 'bg-gray-200/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-300/20 dark:border-gray-600/20'
                    } transition-all duration-300 group-hover:scale-110`}>
                      
                      {/* Background Glow Effect */}
                      {isUnlocked && (
                        <div className={`absolute inset-0 rounded-2xl ${rarityStyle.accent} opacity-20 blur-xl scale-150 group-hover:opacity-30 transition-opacity duration-300`} />
                      )}
                      
                      {/* Main Icon */}
                      <div className="relative z-10">
                        {isUnlocked ? (
                          <CategorySolidIcon className={`h-12 w-12 ${categoryColors[badge.category]} drop-shadow-lg`} />
                        ) : (
                          <LockClosedIcon className="h-12 w-12 text-gray-400" />
                        )}
                      </div>
                      
                      {/* Premium Badge Emoji/Icon */}
                      {isUnlocked && badge.icon && (
                        <div className="absolute -top-2 -right-2 text-4xl drop-shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                          <div className="relative">
                            <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 rounded-full blur-sm" />
                            <span className="relative z-10">{badge.icon}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Sparkle Effect for Legendary */}
                      {isUnlocked && badge.rarity === 'legendary' && (
                        <div className="absolute inset-0 pointer-events-none">
                          <SparklesIcon className="absolute top-1 left-1 h-4 w-4 text-yellow-400 animate-pulse" />
                          <SparklesIcon className="absolute bottom-1 right-1 h-3 w-3 text-orange-400 animate-pulse delay-300" />
                          <SparklesIcon className="absolute top-1 right-1 h-2 w-2 text-amber-400 animate-pulse delay-700" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Premium Badge Info */}
                  <div className="text-center relative z-10">
                    <h3 className={`font-bold text-xl mb-3 tracking-tight ${
                      isUnlocked
                        ? 'text-gray-900 dark:text-white drop-shadow-sm'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {badge.name}
                    </h3>
                    
                    <p className={`text-sm mb-4 leading-relaxed ${
                      isUnlocked
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {badge.description}
                    </p>

                    {/* Premium Progress Bar */}
                    {!isUnlocked && progressData && progressData.maxProgress > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                          <span>İlerleme</span>
                          <span className="font-bold">{progressData.currentProgress}/{progressData.maxProgress}</span>
                        </div>
                        <div className="w-full bg-gray-200/80 dark:bg-gray-700/80 rounded-full h-3 backdrop-blur-sm border border-gray-300/20 dark:border-gray-600/20">
                          <div
                            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500 shadow-lg relative overflow-hidden"
                            style={{ width: `${progressPercentage}%` }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-pulse" />
                          </div>
                        </div>
                        <div className="text-center mt-1">
                          <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                            %{Math.round(progressPercentage)} tamamlandı
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Premium Unlock Date */}
                    {isUnlocked && badge.unlockedAt && (
                      <div className="flex items-center justify-center space-x-1">
                        <CheckCircleIcon className="h-3 w-3 text-green-500" />
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {new Date(badge.unlockedAt).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Premium Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none" />
                  
                  {/* Border Glow Effect */}
                  {isUnlocked && (
                    <div className={`absolute inset-0 rounded-2xl ${rarityStyle.border} border-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />
                  )}
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredBadges.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <TrophyIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Rozet bulunamadı
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Arama kriterlerinizi değiştirmeyi deneyin
            </p>
          </motion.div>
        )}
      </div>

      {/* Premium Badge Detail Modal */}
      <AnimatePresence>
        {selectedBadge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedBadge(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto ${
                selectedBadge.unlockedAt 
                  ? `${rarityColors[selectedBadge.rarity].bg} ${rarityColors[selectedBadge.rarity].border} border-2 ${rarityColors[selectedBadge.rarity].glow}`
                  : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-2 border-gray-200 dark:border-gray-700'
              } rounded-3xl p-8 backdrop-blur-xl shadow-2xl`}
            >
              {/* Modal Background Effects */}
              {selectedBadge.unlockedAt && (
                <>
                  <div className={`absolute inset-0 ${rarityColors[selectedBadge.rarity].shimmer} opacity-5 animate-pulse`} />
                  <div className={`absolute top-0 left-0 w-32 h-32 ${rarityColors[selectedBadge.rarity].accent} opacity-10 rounded-full blur-3xl`} />
                  <div className={`absolute bottom-0 right-0 w-24 h-24 ${rarityColors[selectedBadge.rarity].accent} opacity-10 rounded-full blur-2xl`} />
                </>
              )}
              <div className="text-center relative z-10">
                 {/* Premium Modal Badge Icon */}
                 <div className="flex justify-center mb-8">
                   <div className={`relative p-8 rounded-3xl ${
                     selectedBadge.unlockedAt
                       ? `${rarityColors[selectedBadge.rarity].bg} ${rarityColors[selectedBadge.rarity].border} border-2 ${rarityColors[selectedBadge.rarity].glow} backdrop-blur-sm`
                       : 'bg-gray-100/80 dark:bg-gray-700/80 backdrop-blur-sm border-2 border-gray-200 dark:border-gray-600'
                   } transform hover:scale-105 transition-transform duration-300`}>
                     
                     {/* Modal Background Glow */}
                     {selectedBadge.unlockedAt && (
                       <div className={`absolute inset-0 rounded-3xl ${rarityColors[selectedBadge.rarity].accent} opacity-30 blur-2xl scale-150`} />
                     )}
                     
                     {selectedBadge.unlockedAt ? (
                       <div className="relative z-10">
                         {React.createElement(categorySolidIcons[selectedBadge.category], {
                           className: `h-16 w-16 ${categoryColors[selectedBadge.category]} drop-shadow-xl`
                         })}
                         {selectedBadge.icon && (
                           <div className="absolute -top-3 -right-3 text-5xl drop-shadow-xl">
                             <div className="relative">
                               <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 rounded-full blur-lg scale-150" />
                               <span className="relative z-10">{selectedBadge.icon}</span>
                             </div>
                           </div>
                         )}
                         
                         {/* Enhanced Sparkles for Legendary in Modal */}
                         {selectedBadge.rarity === 'legendary' && (
                           <div className="absolute inset-0 pointer-events-none">
                             <SparklesIcon className="absolute top-2 left-2 h-6 w-6 text-yellow-400 animate-pulse" />
                             <SparklesIcon className="absolute bottom-2 right-2 h-5 w-5 text-orange-400 animate-pulse delay-300" />
                             <SparklesIcon className="absolute top-2 right-2 h-4 w-4 text-amber-400 animate-pulse delay-700" />
                             <SparklesIcon className="absolute bottom-2 left-2 h-3 w-3 text-yellow-500 animate-pulse delay-1000" />
                           </div>
                         )}
                       </div>
                     ) : (
                       <LockClosedIcon className="h-16 w-16 text-gray-400 relative z-10" />
                     )}
                   </div>
                 </div>

                {/* Premium Modal Badge Info */}
                 <h2 className={`text-3xl font-bold mb-3 tracking-tight ${
                   selectedBadge.unlockedAt 
                     ? 'text-gray-900 dark:text-white drop-shadow-lg' 
                     : 'text-gray-600 dark:text-gray-400'
                 }`}>
                   {selectedBadge.name}
                 </h2>
                 
                 <p className={`text-lg leading-relaxed mb-8 ${
                   selectedBadge.unlockedAt 
                     ? 'text-gray-700 dark:text-gray-300' 
                     : 'text-gray-600 dark:text-gray-400'
                 }`}>
                   {selectedBadge.description}
                 </p>

                {/* Rarity and Category */}
                <div className="flex justify-center space-x-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Kategori</p>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">
                      {selectedBadge.category === 'study' && 'Çalışma'}
                      {selectedBadge.category === 'exam' && 'Sınav'}
                      {selectedBadge.category === 'streak' && 'Süreklilik'}
                      {selectedBadge.category === 'achievement' && 'Başarım'}
                      {selectedBadge.category === 'social' && 'Sosyal'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nadir Seviye</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedBadge.rarity === 'common' && 'Yaygın'}
                      {selectedBadge.rarity === 'rare' && 'Nadir'}
                      {selectedBadge.rarity === 'epic' && 'Epik'}
                      {selectedBadge.rarity === 'legendary' && 'Efsanevi'}
                    </p>
                  </div>
                </div>

                {/* Conditions */}
                {selectedBadge.conditions && selectedBadge.conditions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Kazanma Koşulları
                    </h3>
                    <div className="space-y-2">
                      {selectedBadge.conditions.map((condition, index) => (
                        <div key={index} className="text-left p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {condition.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress */}
                {!selectedBadge.unlockedAt && (() => {
                  const progressData = getBadgeProgressData(selectedBadge.id)
                  const progressPercentage = getProgressPercentage(selectedBadge)
                  
                  if (progressData && progressData.maxProgress > 0) {
                    return (
                      <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <span>İlerleme</span>
                          <span>{progressData.currentProgress}/{progressData.maxProgress}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                          <div
                            className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          %{Math.round(progressPercentage)} tamamlandı
                        </p>
                      </div>
                    )
                  }
                  return null
                })()}

                {/* Unlock Date */}
                {selectedBadge.unlockedAt && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Kazanıldı: {new Date(selectedBadge.unlockedAt).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {/* Premium Close Button */}
                 <button
                   onClick={() => setSelectedBadge(null)}
                   className={`w-full px-6 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 active:scale-95 ${
                     selectedBadge.unlockedAt
                       ? `${rarityColors[selectedBadge.rarity].accent} text-white shadow-xl hover:shadow-2xl backdrop-blur-sm`
                       : 'bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 hover:from-gray-300 hover:to-gray-400 dark:hover:from-gray-600 dark:hover:to-gray-500 shadow-lg'
                   }`}
                 >
                   <span className="flex items-center justify-center space-x-2">
                     <span>Kapat</span>
                     {selectedBadge.unlockedAt && selectedBadge.rarity === 'legendary' && (
                       <SparklesIcon className="h-5 w-5 animate-pulse" />
                     )}
                   </span>
                 </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Badges